// Suivi commercial (spec § 11) : lien entre une fiche et son affaire, prochaine action datée.
import { useQuery } from "@tanstack/react-query";
import {
  type Identifier,
  useGetList,
  useLocaleState,
  useNotify,
  useRecordContext,
  useRedirect,
  useTranslate,
} from "ra-core";
import { useState } from "react";
import { Link } from "react-router";
import { DateInput } from "@/components/admin/date-input";
import { SelectInput } from "@/components/admin/select-input";
import { TextInput } from "@/components/admin/text-input";
import { Badge } from "@/components/ui/badge";
import { Card, CardContent } from "@/components/ui/card";

import { findDealLabel } from "../deals/dealUtils";
import { getSupabaseClient } from "../providers/supabase/supabase";
import { useConfigurationContext } from "../root/ConfigurationContext";
import type { Deal, Project } from "../types";
import { DealJournal, LogActivityDialog } from "./DealJournal";
import { DealSuggestions } from "./DealSuggestions";
import {
  DoneButton,
  LinkedInMessageCopy,
  NextActionEditor,
  nextActionText,
  useSaveNextAction,
} from "./NextActionEditor";
import {
  activityKindFor,
  CLEARED_ACTION,
  NEXT_ACTION_TYPES,
  today,
} from "./nextAction";
import { pickProjectDeal } from "./partnerships";
import { useInvited } from "./useInvited";
import { useDealPipeline, usePartnerOrg } from "./usePartnerOrg";

/** Personnes rattachées à la fiche que l'utilisateur voit, pour le journal. */
export const useProjectPeople = (projectId: Identifier) =>
  useQuery({
    queryKey: ["project_people", projectId],
    queryFn: async () => {
      const { data, error } = await getSupabaseClient()
        .from("project_contacts")
        .select("contact_id, contacts(first_name, last_name)")
        .eq("project_id", projectId);
      if (error) throw error;
      return (data ?? []).map((row) => {
        const c = row.contacts as unknown as {
          first_name: string | null;
          last_name: string | null;
        } | null;
        return {
          id: Number(row.contact_id),
          name:
            [c?.first_name, c?.last_name].filter(Boolean).join(" ") ||
            String(row.contact_id),
        };
      });
    },
  });

/** Personnes de l'affaire (ses `contact_ids`), pour le journal de sa vue. */
const useDealPeople = (contactIds: Identifier[] | undefined) =>
  useQuery({
    queryKey: ["deal_people", contactIds],
    enabled: !!contactIds?.length,
    queryFn: async () => {
      const { data, error } = await getSupabaseClient()
        .from("contacts")
        .select("id, first_name, last_name")
        .in("id", contactIds!);
      if (error) throw error;
      return (data ?? []).map((c) => ({
        id: Number(c.id),
        name:
          [c.first_name, c.last_name].filter(Boolean).join(" ") || String(c.id),
      }));
    },
  });

type JournalPerson = { id: number; name: string };

/** Prochaine action avec « Fait » : noter l'échange (type prérempli), puis proposer la suite. Regroupe les deux
 * fenêtres pour la fiche, la vue de l'affaire et le bouton fixe du téléphone. */
const useActionFlow = () => {
  const [editorOpen, setEditorOpen] = useState(false);
  const [doneOpen, setDoneOpen] = useState(false);
  return { editorOpen, setEditorOpen, doneOpen, setDoneOpen };
};

/** « Fait » : l'échange noté, l'action accomplie est retirée, puis l'éditeur s'ouvre vide pour la suivante
 * (spec § 4). */
const DoneDialog = ({
  deal,
  people,
  open,
  onOpenChange,
  onLogged,
}: {
  deal: Deal;
  people: JournalPerson[];
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onLogged: () => void;
}) => {
  const { save } = useSaveNextAction();
  return (
    <LogActivityDialog
      deal={deal}
      contacts={people}
      open={open}
      onOpenChange={onOpenChange}
      onLogged={async () => {
        if (await save(deal.id, CLEARED_ACTION, { quiet: true })) onLogged();
      }}
      preset={{
        kind: activityKindFor(deal.next_action_type),
        contact_id: deal.next_action_contact_id
          ? Number(deal.next_action_contact_id)
          : null,
      }}
    />
  );
};

/** Action datée d'avant aujourd'hui ; une affaire archivée n'a plus d'action en retard. */
const isOverdue = (deal: Pick<Deal, "next_action_date" | "archived_at">) =>
  !!deal.next_action_date &&
  !deal.archived_at &&
  deal.next_action_date < today();

// Date seule (AAAA-MM-JJ) lue sans décalage de fuseau.
const formatDate = (date: string, locale: string) => {
  const [year, month, day] = date.split("-").map(Number);
  return new Date(year, month - 1, day).toLocaleDateString(locale);
};

/** Prochaine action et sa date, en rouge quand elle est dépassée. */
export const NextAction = ({
  deal,
  compact = false,
}: {
  deal: Deal;
  compact?: boolean;
}) => {
  const translate = useTranslate();
  const [locale = "fr"] = useLocaleState();
  if (!deal.next_action && !deal.next_action_type && !deal.next_action_date) {
    return compact ? null : (
      <span className="text-sm text-muted-foreground">
        {translate("resources.deals.follow_up.no_action")}
      </span>
    );
  }
  const late = isOverdue(deal);
  return (
    <span
      className={`${compact ? "text-xs" : "text-sm"} inline-flex flex-wrap gap-x-2 gap-y-1 items-center`}
    >
      {deal.next_action_date && (
        <span className={late ? "text-destructive font-medium" : ""}>
          {formatDate(deal.next_action_date, locale)}
        </span>
      )}
      {nextActionText(deal, translate) && (
        <span>{nextActionText(deal, translate)}</span>
      )}
      {!deal.next_action_date && !deal.archived_at && (
        <span className="text-muted-foreground">
          {translate("resources.deals.next_action_editor.none")}
        </span>
      )}
      {late && !compact && (
        <Badge variant="destructive">
          {translate("resources.deals.follow_up.overdue")}
        </Badge>
      )}
      {!compact &&
        deal.next_action_type === "linkedin_dm" &&
        deal.next_action_message && (
          <LinkedInMessageCopy message={deal.next_action_message} />
        )}
    </span>
  );
};

/** Bloc de la vue d'une affaire : fiche suivie et prochaine action. */
export const DealFollowUp = () => {
  const translate = useTranslate();
  const record = useRecordContext<Deal>();
  const invited = useInvited();
  const { data: people } = useDealPeople(record?.contact_ids);
  const flow = useActionFlow();
  if (!record) return null;
  return (
    <div className="flex flex-wrap gap-8 m-4">
      <div className="flex flex-col gap-1">
        <span className="text-xs text-muted-foreground tracking-wide">
          {translate("resources.deals.fields.next_action")}
        </span>
        <NextAction deal={record} />
        {!invited && !record.archived_at && (
          <div className="flex flex-wrap gap-2">
            <NextActionEditor
              deal={record}
              open={flow.editorOpen}
              onOpenChange={flow.setEditorOpen}
            />
            {record.next_action_date && (
              <DoneButton onClick={() => flow.setDoneOpen(true)} />
            )}
          </div>
        )}
        {!invited && !record.archived_at && (
          <DoneDialog
            deal={record}
            people={people ?? []}
            open={flow.doneOpen}
            onOpenChange={flow.setDoneOpen}
            onLogged={() => flow.setEditorOpen(true)}
          />
        )}
      </div>
      {record.project_id && (
        <div className="flex flex-col gap-1">
          <span className="text-xs text-muted-foreground tracking-wide">
            {translate("resources.deals.fields.project_id")}
          </span>
          <Link
            to={`/projects/${record.project_id}/show`}
            className="text-sm underline"
          >
            {translate("resources.deals.follow_up.open_project")}
          </Link>
        </div>
      )}
      <div className="basis-full">
        <DealJournal
          deal={record}
          contacts={people ?? []}
          canWrite={!invited && !record.archived_at}
          onAskNext={() => flow.setEditorOpen(true)}
        />
      </div>
    </div>
  );
};

/** Champs du formulaire d'une affaire. */
export const DealFollowUpInputs = () => (
  <>
    <SelectInput
      source="next_action_type"
      choices={NEXT_ACTION_TYPES.map((id) => ({
        id,
        name: `resources.deals.next_action_types.${id}`,
      }))}
      helperText={false}
    />
    <TextInput source="next_action" helperText={false} />
    <DateInput source="next_action_date" helperText={false} />
  </>
);

/** Affaire liée à la fiche et création de l'affaire, partagées par le bloc et la barre d'actions. */
export const useProjectDeal = (project: Project) => {
  const notify = useNotify();
  const redirect = useRedirect();
  const { dealStages } = useConfigurationContext();
  const [busy, setBusy] = useState(false);
  const { sale } = usePartnerOrg();
  // Plusieurs affaires visibles (la mienne et celle qu'un hôte m'a transmise) : la mienne d'abord.
  const {
    data: deals,
    isPending,
    refetch,
  } = useGetList<Deal>("deals", {
    filter: { project_id: project.id },
    pagination: { page: 1, perPage: 20 },
    sort: { field: "id", order: "ASC" },
  });
  const follow = async () => {
    setBusy(true);
    const { data, error } = await getSupabaseClient().rpc("follow_project", {
      project: project.id,
      first_stage: dealStages[0].value,
    });
    setBusy(false);
    if (error) {
      notify(error.message, { type: "error" });
      return;
    }
    redirect(`/deals/${data}/show`);
  };
  return {
    deal: pickProjectDeal(deals ?? [], sale?.id, sale?.org_id),
    isPending,
    busy,
    follow,
    refetch,
  };
};

/** Bloc « Suivi commercial » de la fiche : l'affaire liée et sa prochaine action ; le bouton est dans la barre d'actions. */
export const ProjectFollowUp = ({ project }: { project: Project }) => {
  const translate = useTranslate();
  const { deal, isPending } = useProjectDeal(project);
  const invited = useInvited();
  const { data: people } = useProjectPeople(project.id);
  const flow = useActionFlow();
  // Étapes du pipeline de l'affaire (organisation partenaire ou client).
  const { dealStages } = useDealPipeline(deal);
  // Partenaires qui suivent la fiche de leur côté : leur nom seulement, l'affaire leur reste propre. La base ne
  // renvoie rien à un partenaire.
  const { data: partners } = useQuery({
    queryKey: ["project_partner_followers", project.id],
    queryFn: async () => {
      const { data, error } = await getSupabaseClient().rpc(
        "project_partner_followers",
        { project: project.id },
      );
      if (error) throw error;
      return (data ?? []) as { first_name: string; last_name: string }[];
    },
  });
  // Sans affaire ni partenaire, rien à dire : le bouton « Suivre ce projet » est déjà en bas de la fiche.
  if (isPending || (!deal && !partners?.length)) return null;

  return (
    <Card>
      <CardContent className="flex flex-col gap-3">
        <h3 className="text-sm font-bold">
          {translate("resources.deals.follow_up.title")}
        </h3>
        {deal ? (
          <div className="flex flex-col gap-2 text-sm">
            <div className="flex flex-wrap gap-2 items-center">
              <Badge variant="secondary">
                {findDealLabel(dealStages, deal.stage) ?? deal.stage}
              </Badge>
              {deal.archived_at && (
                <Badge variant="outline">
                  {translate("resources.deals.archived.title")}
                </Badge>
              )}
            </div>
            <NextAction deal={deal} />
            {!deal.archived_at && (
              <DealSuggestions dealIds={[deal.id]} canWrite={!invited} />
            )}
            {!deal.archived_at && !invited && (
              <div className="flex flex-wrap gap-2">
                <NextActionEditor
                  deal={deal}
                  open={flow.editorOpen}
                  onOpenChange={flow.setEditorOpen}
                />
                {deal.next_action_date && (
                  <DoneButton onClick={() => flow.setDoneOpen(true)} />
                )}
              </div>
            )}
            <DealJournal
              deal={deal}
              contacts={people ?? []}
              canWrite={!invited && !deal.archived_at}
              onAskNext={() => flow.setEditorOpen(true)}
            />
            {!invited && !deal.archived_at && (
              <>
                <DoneDialog
                  deal={deal}
                  people={people ?? []}
                  open={flow.doneOpen}
                  onOpenChange={flow.setDoneOpen}
                  onLogged={() => flow.setEditorOpen(true)}
                />
              </>
            )}
          </div>
        ) : (
          <p className="text-sm text-muted-foreground">
            {translate("resources.deals.follow_up.help")}
          </p>
        )}
        {!!partners?.length && (
          <p className="text-sm text-muted-foreground">
            {translate("resources.deals.follow_up.partners_following", {
              smart_count: partners.length,
              names: partners
                .map((p) => `${p.first_name} ${p.last_name}`)
                .join(", "),
            })}
          </p>
        )}
      </CardContent>
    </Card>
  );
};
